import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    LinearProgress,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../ui/ToastProvider';
import { extractApiErrorMessage } from '../api/httpClient';
import { updatePerfilInitialPassword } from '../api/clients/perfilClient';

function evaluatePasswordCriteria(password) {
    const pwd = typeof password === 'string' ? password : '';
    return {
        minLength: pwd.length >= 10,
        hasUpper: /[A-Z]/.test(pwd),
        hasLower: /[a-z]/.test(pwd),
        hasNumber: /\d/.test(pwd),
        hasSymbol: /[^A-Za-z0-9]/.test(pwd),
    };
}

export default function InitialPasswordDialog() {
    const { user, token, revalidateSession, logout } = useAuth();
    const toast = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const criteria = useMemo(() => evaluatePasswordCriteria(password), [password]);

    const criteriaScore = useMemo(() => {
        let score = 0;
        if (criteria.minLength) score += 1;
        if (criteria.hasUpper) score += 1;
        if (criteria.hasLower) score += 1;
        if (criteria.hasNumber) score += 1;
        if (criteria.hasSymbol) score += 1;
        return score;
    }, [criteria]);

    const isAllCriteriaMet = criteriaScore === 5;
    const passwordsMatch = password && passwordConfirmation && password === passwordConfirmation;

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFormError('');

        if (!token) {
            setFormError('Sesión no disponible. Por favor, vuelve a iniciar sesión.');
            return;
        }

        if (!isAllCriteriaMet) {
            setFormError('La nueva contraseña debe cumplir con todos los requisitos de seguridad.');
            return;
        }

        if (!passwordConfirmation) {
            setFormError('Debes confirmar la nueva contraseña.');
            return;
        }

        if (password !== passwordConfirmation) {
            setFormError('Las contraseñas no coinciden.');
            return;
        }

        try {
            setSaving(true);
            await updatePerfilInitialPassword(token, {
                password,
                password_confirmation: passwordConfirmation,
            });

            toast.success('¡Contraseña actualizada con éxito! Bienvenido al sistema.');
            await revalidateSession();
        } catch (error) {
            const msg = extractApiErrorMessage(error, 'No se pudo actualizar la contraseña inicial.');
            setFormError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const criteriaItems = [
        { key: 'minLength', label: 'Mínimo 10 caracteres', met: criteria.minLength },
        { key: 'hasUpper', label: 'Al menos una letra mayúscula (A-Z)', met: criteria.hasUpper },
        { key: 'hasLower', label: 'Al menos una letra minúscula (a-z)', met: criteria.hasLower },
        { key: 'hasNumber', label: 'Al menos un número (0-9)', met: criteria.hasNumber },
        { key: 'hasSymbol', label: 'Al menos un símbolo o carácter especial (!@#$...)', met: criteria.hasSymbol },
    ];

    return (
        <Dialog
            open={true}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    p: { xs: 1, sm: 2 },
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1, pt: { xs: '44px', sm: 2 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <LockResetRoundedIcon fontSize="medium" />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            Actualización obligatoria de contraseña
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Primer inicio de sesión · {user?.name || 'Técnico'}
                        </Typography>
                    </Box>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 2.5 }}>
                <Box component="form" id="initial-password-form" onSubmit={handleSubmit}>
                    <Stack spacing={2.5}>
                        <Alert severity="info" icon={<SecurityRoundedIcon fontSize="inherit" />} sx={{ borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Por motivos de seguridad, debes cambiar tu contraseña temporal provisoria antes de acceder al sistema.
                            </Typography>
                        </Alert>

                        {formError ? (
                            <Alert severity="error" sx={{ borderRadius: 2 }}>
                                {formError}
                            </Alert>
                        ) : null}

                        {/* Campo Nueva Contraseña */}
                        <TextField
                            size="small"
                            fullWidth
                            required
                            label="Nueva contraseña"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            disabled={saving}
                            autoComplete="new-password"
                            autoFocus
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                                            size="small"
                                            edge="end"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Barra de progreso de seguridad */}
                        {password ? (
                            <Box sx={{ mt: -1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        Seguridad de la contraseña
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 800,
                                            color: criteriaScore <= 2 ? 'error.main' : criteriaScore <= 4 ? 'warning.main' : 'success.main',
                                            ml: 2
                                        }}
                                    >
                                        {criteriaScore <= 2 ? 'Débil' : criteriaScore <= 4 ? 'Media' : 'Fuerte y segura'}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={(criteriaScore / 5) * 100}
                                    color={criteriaScore <= 2 ? 'error' : criteriaScore <= 4 ? 'warning' : 'success'}
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                            </Box>
                        ) : null}

                        {/* Checklist interactivo de requisitos */}
                        <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: alpha(theme.palette.background.default, 0.6) }}>
                            <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', display: 'block', mb: 1 }}>
                                    Requisitos de seguridad obligatorios
                                </Typography>
                                <Stack spacing={0.75}>
                                    {criteriaItems.map((item) => (
                                        <Stack key={item.key} direction="row" spacing={1} alignItems="center">
                                            {item.met ? (
                                                <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18, flexShrink: 0 }} />
                                            ) : (
                                                <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
                                            )}
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: '0.815rem',
                                                    fontWeight: item.met ? 700 : 500,
                                                    color: item.met ? 'text.primary' : 'text.secondary',
                                                }}
                                            >
                                                {item.label}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* Campo Confirmar Contraseña */}
                        <TextField
                            size="small"
                            fullWidth
                            required
                            label="Confirmar nueva contraseña"
                            type={showConfirmation ? 'text' : 'password'}
                            value={passwordConfirmation}
                            disabled={saving}
                            autoComplete="new-password"
                            error={Boolean(password && passwordConfirmation && password !== passwordConfirmation)}
                            helperText={
                                password && passwordConfirmation && password !== passwordConfirmation
                                    ? 'Las contraseñas no coinciden'
                                    : ''
                            }
                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showConfirmation ? 'Ocultar contraseña' : 'Ver contraseña'}
                                            size="small"
                                            edge="end"
                                            onClick={() => setShowConfirmation((prev) => !prev)}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            {showConfirmation ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    startIcon={<LogoutRoundedIcon />}
                    onClick={logout}
                    disabled={saving}
                    sx={{ color: 'text.secondary' }}
                >
                    Cerrar sesión
                </Button>

                <Button
                    type="submit"
                    form="initial-password-form"
                    variant="contained"
                    color="primary"
                    disabled={saving || !isAllCriteriaMet || !passwordsMatch}
                    sx={{ fontWeight: 700, px: 3, py: 0.9, borderRadius: 1.75 }}
                >
                    {saving ? 'Guardando contraseña...' : 'Establecer contraseña y continuar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
